<template>
  <Popup
      @close="$emit('closePopup',true)"
      :show-footer="false"
      width="720px"
      body-padding="0 32px 32px"
      header-padding="16px 32px 32px"
      modal-header-class="modal-header-border"
  >
    <template v-slot:title>Members</template>
    <template v-slot:subTitle><span class="project-label">{{ projectName }}</span></template>
    <template v-slot:body>
      <div class="body-wrapper">
        <div class="header" v-if="hasAdministrativeRole">
          <div class="input-area">
            <InputElement
                v-model="search"
                name="search"
                type="email"
                placeholder="Type a name or email address"
            />
            <ul class="dropdown-menu datatable-members" :class="search.length>0 ? 'show' : ''">
              <div class="dropdown-menu-box animation">
                <li class="member" v-if="getFilteredWorkSpaceMembers.length == 0">
                  No member found. Please invite by email.
                </li>

                <li class="member" v-for="(wsMember, index) in getFilteredWorkSpaceMembers"
                    :key="index"
                    @click="addMemberToProject(wsMember)">
                  <div class="user">
                    <div class="user-avatar">
                      <AvatarWidget
                          :image-key="wsMember.avatar"
                          :image-url="wsMember.avatar"
                          image-size="imageSM"
                          :full-name-avatar="wsMember.fullNameAvatar"
                          :alt="wsMember.fullNameAvatar"
                          :title="wsMember.fullNameAvatar">
                      </AvatarWidget>
                    </div>
                    <div class="info">
                      <span class="name">{{ wsMember.fullName }}</span>
                      <span class="mail">{{ wsMember.userName }}</span>
                    </div>
                  </div>
                </li>
              </div>
            </ul>
          </div>
          <div class="action-area">
            <div class="action">
              <SelectableDropdownElement
                  v-model:value="selectedProjectRole"
                  title="Members"
                  type="buttonselect"
                  name="roles"
                  placeholder="Roles"
                  :options="projectRoles"
                  dropdowncss="dropdown-menu-large"
                  :is-disable="isInvitationDisabled()"
              />
            </div>
            <div class="action">
              <ButtonElement
                  size="lg"
                  class="btn--ms btn--accent"
                  @click="sendInvite"
                  :is-disable="isInvitationDisabled()"
              >Send Invite
              </ButtonElement>
            </div>
          </div>
        </div>
        <div class="content">
          <DataTable
              :value="getFilteredProjectMembers"
              :paginator="getFilteredProjectMembers.length > 5"
              :rows="5"
              paginator-template=" PageLinks  "
              :rows-per-page-options="[5,10,15]"
              current-page-report-template="Showing {first} to {last} of {totalRecords}"
              class="datatable-members"
              responsive-layout="scroll"
          >
            <Column header class="datacol-1">
              <template #body="slotProps">
                <div class="user">
                  <div class="user-avatar">
                    <AvatarWidget
                        :image-key="slotProps.data.avatar"
                        :image-url="slotProps.data.avatar"
                        :social-avatar="slotProps.data.socialAvatar"
                        image-size="imageSM"
                        :full-name-avatar="slotProps.data.fullNameAvatar"
                        :alt="slotProps.data.fullNameAvatar"
                        :title="slotProps.data.fullNameAvatar">
                    </AvatarWidget>
                  </div>
                  <div class="info">
                    <span class="name">{{ slotProps.data.fullName }}</span>
                    <span class="mail">{{ slotProps.data.userName }}</span>
                  </div>
                </div>
              </template>
            </Column>
            <Column header class="datacol-2">
              <template #body="slotProps">
                <TagElement :class="getRoleTagClass(slotProps.data.workspaceRole)">
                  {{ formatRole(slotProps.data.workspaceRole) }}
                </TagElement>
              </template>
            </Column>
            <Column header class="datacol-2">
              <template #body="slotProps">
                <TagElement class="tag--neutral">{{ formatStatus(slotProps.data.status) }}</TagElement>
              </template>
            </Column>
            <Column header class="datacol-3">
              <template #body="slotProps">
                <SelectableDropdownElement
                    v-if="hasAdministrativeRole && slotProps.data.workspaceRole === 'MEMBER'"
                    v-model:value="slotProps.data.projectRole"
                    @change="changeMemberRole($event,slotProps.data.userName)"
                    title="Members"
                    type="buttonselect"
                    name="roles"
                    placeholder="Roles"
                    :options="projectRoles"
                    :is-disable="slotProps.data.status === 'PENDING'"
                    dropdowncss="dropdown-menu-large"
                />
              </template>
            </Column>
            <Column header class="datacol-3">
              <template #body="slotProps">
                <ButtonElement
                    v-if="hasAdministrativeRole && slotProps.data.workspaceRole === 'MEMBER'"
                    class="btn--ms btn--silent-tertiary "
                    @click="removeProjectMember(slotProps.data.userName)"
                    data-tooltip-position="top"
                    data-tooltip="Remove"
                >
                  <LucideIcon icon="X"/>
                </ButtonElement>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Popup>
</template>
<script lang="ts">
import {defineComponent, type PropType} from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import {ButtonElement, InputElement, SelectableDropdownElement, TagElement,} from '@shared/components/elements';
import {type DropdownNs, ProjectNs, WorkspaceNs,} from '@shared/types';
import Popup from '@shared/components/elements/popup/popup.element.vue';
import {isWsOwnerOrAdmin} from "@shared/mixins/role-manager";
import {mapStores} from "pinia";
import {useWsMemberStore} from "@domains/settings/stores/workspace-member.store";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useProjectStore} from "@domains/dashboard/stores/project.store";
import {isValidEmail} from "@shared/helpers";
import {useGeneralStore} from "@shared/stores/general.store";
import AvatarWidget from "@shared/components/widgets/profile/avatar.widget.vue";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'MembersModalWidget',
  components: {
    AvatarWidget,
    ButtonElement,
    DataTable,
    Column,
    Popup,
    InputElement,
    SelectableDropdownElement,
    TagElement,
  },
  emits: ['closePopup'],
  props: {
    projectId: {
      type: String,
      required: true,
    },
    projectName: {
      type: String,
      required: true,
    },
    projectMemberList: {
      type: Object as PropType<ProjectNs.Member[]>,
      required: true,
    },
  },
  computed: {
    ...mapStores(useWorkspaceStore, useWsMemberStore, useProjectStore, useGeneralStore),
    hasAdministrativeRole(): boolean {
      return isWsOwnerOrAdmin() || (this.project.projectRole === ProjectNs.ProjectRoleEnums.MANAGER);
    },
    getFilteredWorkSpaceMembers(): WorkspaceNs.Member[] {
      const wsMembers = this.wsMemberStore.workspaceMembers;
      if (wsMembers && wsMembers.length > 0) {
        const userNameInProject: string[] = this.memberList.map((y: ProjectNs.Member) => y.userName);
        const wsMemberList = wsMembers.filter((x: WorkspaceNs.Member) => !userNameInProject.includes(x.userName));

        // for search members in the future
        if (!this.search) return wsMemberList;
        const all = [...wsMemberList];
        const searchQuery: string = this.search || '';
        const searchKey = searchQuery.toLocaleLowerCase();
        return all.filter((member) => {
          const fullName = member.fullName.toLocaleLowerCase();
          const userName = member.userName.toLocaleLowerCase();
          if (
              fullName.includes(searchKey)
              || userName.includes(searchKey)
          ) {
            return true;
          }
          return false;
        });
      } else {
        return [] as WorkspaceNs.Member[];
      }
    },
    getFilteredProjectMembers(): any[] {
      const wsMembers = this.wsMemberStore.workspaceMembers;
      if (!wsMembers || wsMembers.length === 0) return [];

      return this.memberList.map((pMember: ProjectNs.Member) => {
        const projectMember: any = {...pMember};
        const wsMember = wsMembers.find((wsMem: WorkspaceNs.Member) => wsMem.userName === pMember.userName);
        if (wsMember) {
          projectMember.workspaceRole = wsMember.workspaceRole;
        }
        return projectMember;
      });
    }
  },
  beforeMount() {
    this.memberList = [...this.projectMemberList];
    this.loadWorkspaceMembers();
    this.loadProject(this.projectId);
  },
  data() {
    return {
      memberList: [] as ProjectNs.Member[],
      search: '',
      selectedProjectRole: 'VIEWER',
      project: {} as ProjectNs.Project,
      projectRoles: [
        {
          iconType: 'fal',
          icon: 'ShieldCheck',
          name: 'Viewer',
          value: 'VIEWER',
          content: 'Can just view the project.',
        },
        {
          iconType: 'fal',
          icon: 'UserCog',
          name: 'Editor',
          value: 'EDITOR',
          content: 'Can view and edit project.',
        },
        {
          iconType: 'fal',
          icon: 'Users',
          name: 'Manager',
          value: 'MANAGER',
          content: 'Can view, edit and add members to the project.',
        },
      ] as DropdownNs.DropdownOptionsItem[],
      memberActions: [
        {
          name: 'Details',
          value: 'DETAILS',
          iconType: 'fal',
          icon: 'UserCog',
        },
        {
          name: 'Disabled',
          value: 'DISABLED',
          iconType: 'fal',
          icon: 'UserX',
        },
        {
          name: 'Resend Invitation',
          value: 'RESEND_INVITATION',
          iconType: 'fal',
          icon: 'Send',
        }
      ] as DropdownNs.DropdownOptionsItem[],
    };
  },
  methods: {
    formatStatus(status: string): string {
      if (!status) return '';
      const upper = status.toUpperCase();
      if (upper === 'ACTIVE') return 'Active';
      if (upper === 'INACTIVE') return 'Not active';
      if (upper === 'PENDING') return 'Pending';
      return status;
    },
    formatRole(role: string): string {
      if (!role) return '';
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    },
    getRoleTagClass(role: string): string {
      const roleUpper = role?.toUpperCase();
      if (roleUpper === 'OWNER') return 'tag--orange';
      if (roleUpper === 'ADMIN') return 'tag--blue';
      if (roleUpper === 'MEMBER') return 'tag--neutral';
      return 'tag--neutral'; // fallback
    },
    addMemberToProject(wsMember: WorkspaceNs.Member) {
      this.search = '';
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        projectId: this.projectId,
        userName: wsMember.userName,
        projectRole: 'VIEWER',
      } as ProjectNs.ChangeProjectMemberStatusRequest;

      this.wsMemberStore.addMemberToProject(payload).then((newMember) => {
        this.memberList.push(newMember);
      });
    },
    loadWorkspaceMembers() {
      const workspaceId: string = this.workspaceStore.getSelectedWorkspaceId;
      return this.wsMemberStore.loadWorkspaceMembers(workspaceId);
    },
    loadProject(projectId: string) {
      this.projectStore.loadProject(projectId).then((response) => {
        this.project = response;
      });
    },

    async sendInvite() {
      if (!this.search || this.search.length === 0 || !this.selectedProjectRole) {
        return;
      }

      if (!isValidEmail(this.search)) {
        
        toast.warning('Enter a valid email address');
      }

      const payload = {
        emailAddresses: [{email: this.search, projectRole: this.selectedProjectRole}],
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        projectId: this.projectId,
      };

      const response = await this.wsMemberStore.inviteMemberToWorkspace(payload);
      if (response.result) {
        
        toast.success('Invitation sent');

        this.memberList.push({
          id: Math.random(),
          fullNameAvatar: this.search.substring(0, 2),
          projectRole: this.selectedProjectRole,
          workspaceRole: 'MEMBER',
          userName: this.search,
          status: 'PENDING'
        } as ProjectNs.Member)

        this.search = '';
        this.selectedProjectRole = 'VIEWER';
      }
    },
    changeMemberRole(
        selectedRole: DropdownNs.DropdownOptionsItem,
        selectedMemberName: string,
    ) {
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        projectId: this.projectId,
        userName: selectedMemberName,
        projectRole: selectedRole.value,
      } as ProjectNs.ChangeProjectMemberStatusRequest;

      this.wsMemberStore.changeProjectMemberRole(payload).then(() => {
      });
    },
    removeProjectMember(
        selectedMemberName: string,
    ) {
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        projectId: this.projectId,
        userName: selectedMemberName,
      } as ProjectNs.ChangeProjectMemberStatusRequest;

      this.wsMemberStore.deleteMemberFromProject(payload).then(() => {
        this.memberList = this.memberList.filter((item: any) => item.userName !== selectedMemberName);
      });
    },

    isInvitationDisabled(): boolean {
      return this.search.length > 0 && this.getFilteredWorkSpaceMembers.length === 0 && !isValidEmail(this.search);
    },
  },
});
</script>
<style lang="scss" scoped>
.body-wrapper {
  .header {
    display: flex;
    align-items: center;
    gap: var(--space-16);
    position: relative;
    padding: 0 0 15px 0;

    .input-area {
      flex-basis: 60%;
      position: relative;

      .dropdown-menu {
        width: 100%;
        margin-top: 3px;

        .dropdown-menu-box{
          padding: var(--space-8);
        }

        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          color: var(--accent-100);
          margin: 10px 0;
        }

        .member {
          background-color: var(--transparent);
          border: 1px solid var(--transparent);
          padding: var(--space-8);
          color: var(--text-neutral);
          font-size: var(--fs-12);
          border-radius: $br-sm;

          &:hover {
            background-color: var(--item-blue-bg-hover);
            border: 1px solid var(--item-blue-border-hover);

            .name, .mail {
              text-decoration: none;
            }
          }
        }
      }
    }

    .action-area {
      flex-basis: 40%;
      display: flex;
      align-items: center;

      .action:first-child {
        flex: 1;
      }

      gap: 10px;
    }
  }
}

.p-datatable-wrapper{
  margin: var(--space-0) !important;
}
.project-label {
  color: var(--text-neutral) !important;
}

</style>
